import { CheckIcon, ExternalLinkIcon, MinusIcon } from "lucide-react";
import { StatusPill, type StatusTone } from "@/components/status-pill";
import { SimpleTooltip } from "@/components/ui/primitives";
import { cn } from "@/lib/utils";
import { RecordLink, type RecordTarget } from "./links";
import type { EntityRecord, FieldMeta, OptionTone } from "./types";

/* ------------------------------------------------------------ formatters */
const dateFmt = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });
const dateTimeFmt = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Parse "2026-10-18" as a local calendar date (not UTC midnight). */
function parseDate(value: unknown): Date | null {
  if (typeof value !== "string" || !value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatMoney(value: unknown, currency = "GBP"): string {
  const n = toNumber(value);
  if (n === null) return "";
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return n.toFixed(2);
  }
}

export function formatHours(value: unknown): string {
  const n = toNumber(value);
  if (n === null) return "";
  return `${new Intl.NumberFormat("en-GB", { maximumFractionDigits: 2 }).format(n)} h`;
}

export function formatDateValue(value: unknown): string {
  const d = parseDate(value);
  return d ? dateFmt.format(d) : "";
}

/** "18 Oct 2026, 14:05" */
export function formatDateTimeValue(value: unknown): string {
  const d = parseDate(value);
  return d ? dateTimeFmt.format(d) : "";
}

export function optionFor(field: FieldMeta, value: unknown) {
  return field.options?.find((o) => o.value === value);
}

export function toneFor(tone: OptionTone | null | undefined): StatusTone {
  switch (tone) {
    case "info":
    case "success":
    case "warning":
    case "danger":
    case "neutral":
    case "accent":
      return tone;
    default:
      return "neutral";
  }
}

export function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === "";
}

/** Plain-text rendering of a field value (CSV export, conflict dialog, titles). */
export function formatPlain(field: FieldMeta, record: EntityRecord | null, value: unknown = record?.[field.name]): string {
  if (isEmpty(value)) return "";
  switch (field.type) {
    case "enum":
      return optionFor(field, value)?.label ?? String(value);
    case "lookup": {
      const label = record?._display?.[field.name];
      return label ?? String(value);
    }
    case "money":
      return formatMoney(value, field.currency ?? "GBP");
    case "hours":
      return formatHours(value);
    case "date":
      return formatDateValue(value);
    case "datetime":
      return formatDateTimeValue(value);
    case "bool":
      return value === true || value === "true" ? "Yes" : "No";
    case "int":
    case "decimal": {
      const n = toNumber(value);
      return n === null ? String(value) : new Intl.NumberFormat("en-GB", { maximumFractionDigits: 6 }).format(n);
    }
    default:
      return String(value);
  }
}

/* ------------------------------------------------------------ renderer */
interface FieldValueProps {
  field: FieldMeta;
  record: EntityRecord;
  /** Link resolver for lookups; when it returns null the label is plain text. */
  linkFor?: (entity: string, id: string) => RecordTarget | null;
  /** Table cell: truncate long text. */
  compact?: boolean;
}

const Empty = () => <span className="text-muted-foreground/60">—</span>;

export function FieldValue({ field, record, linkFor, compact }: FieldValueProps) {
  const value = record[field.name];
  if (field.type === "bool") {
    const on = value === true || value === "true";
    return on ? (
      <CheckIcon className="size-4 text-success" aria-label="Yes" />
    ) : compact ? (
      <span className="sr-only">No</span>
    ) : (
      <MinusIcon className="size-4 text-muted-foreground/60" aria-label="No" />
    );
  }
  if (isEmpty(value)) return <Empty />;

  switch (field.type) {
    case "enum": {
      const opt = optionFor(field, value);
      return <StatusPill tone={toneFor(opt?.tone)}>{opt?.label ?? String(value)}</StatusPill>;
    }
    case "lookup": {
      const label = record._display?.[field.name] ?? String(value);
      const target = field.lookup && linkFor ? linkFor(field.lookup.entity, String(value)) : null;
      return target ? (
        <RecordLink target={target} className={cn(compact && "block max-w-56 truncate")}>
          {label}
        </RecordLink>
      ) : (
        <span className={cn(compact && "block max-w-56 truncate")}>{label}</span>
      );
    }
    case "money":
    case "hours":
    case "int":
    case "decimal":
      return <span className="tabular">{formatPlain(field, record)}</span>;
    case "date":
    case "datetime":
      return <span className="tabular whitespace-nowrap">{formatPlain(field, record)}</span>;
    case "url":
      return (
        <a
          href={String(value)}
          target="_blank"
          rel="noreferrer noopener"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex max-w-64 items-center gap-1 text-primary underline-offset-4 hover:underline"
        >
          <span className="truncate">{String(value).replace(/^https?:\/\//, "")}</span>
          <ExternalLinkIcon className="size-3 shrink-0" aria-hidden="true" />
        </a>
      );
    case "email":
      return (
        <a
          href={`mailto:${String(value)}`}
          onClick={(e) => e.stopPropagation()}
          className="text-primary underline-offset-4 hover:underline"
        >
          {String(value)}
        </a>
      );
    case "text":
    case "richtext": {
      const text = String(value);
      if (!compact) return <span className="whitespace-pre-line">{text}</span>;
      return text.length > 60 ? (
        <SimpleTooltip label={<span className="block max-w-xs whitespace-pre-line">{text.slice(0, 400)}</span>}>
          <span className="block max-w-72 truncate">{text}</span>
        </SimpleTooltip>
      ) : (
        <span className="block max-w-72 truncate">{text}</span>
      );
    }
    default:
      return <span className={cn(compact && "block max-w-72 truncate")}>{String(value)}</span>;
  }
}

export function isNumericType(type: FieldMeta["type"]): boolean {
  return type === "int" || type === "decimal" || type === "money" || type === "hours";
}
